import { ORACLE_ACCOUNT_PREFIX } from '@erp-attach/shared';
import type { EntityType } from '@erp-attach/shared';
import type { SearchResult } from '../api/types';

export const routes = {
  customer: (accountNumber: string) => `/customers/${encodeURIComponent(accountNumber)}`,
  job: (siteId: string) => `/jobs/${encodeURIComponent(siteId)}`,
  /** Resolver: account + party site number → redirects to `job(siteId)` (the Collections link, spec 2026-10-20). */
  customerSite: (accountNumber: string, partySiteNumber: string) =>
    `/customers/${encodeURIComponent(accountNumber)}/sites/${encodeURIComponent(partySiteNumber)}`,
  supplier: (supplierNumber: string) => `/suppliers/${encodeURIComponent(supplierNumber)}`,
  /** A document number (preferred: /d/10482) or, for old links, a UUID. */
  document: (ref: string | number) => `/d/${encodeURIComponent(String(ref))}`,
  search: (q: string) => `/search?${new URLSearchParams({ q }).toString()}`,
  browseCustomers: '/browse',
  browseSuppliers: '/browse?kind=supplier',
} as const;

export function entityRoute(kind: EntityType, number: string): string {
  switch (kind) {
    case 'customer':
      return routes.customer(number);
    case 'site':
      return routes.job(number);
    case 'supplier':
      return routes.supplier(number);
  }
}

export function searchResultHref(r: SearchResult): string {
  return r.kind === 'document' ? routes.document(r.number || r.id) : entityRoute(r.kind, r.number);
}

const PATH_RE = /\/(customers|jobs|suppliers|d)\/([^/?#]+)/;

/**
 * Resolves something pasted into the home box into an in-app path:
 *   - a deep link (any host) containing /customers/…, /jobs/…, /suppliers/… or /d/…
 *   - a bare Oracle account number (20000…) → customer page
 * Returns null when the input should be treated as a search instead.
 */
export function resolvePastedLink(input: string): string | null {
  const text = input.trim();
  if (!text) return null;
  const m = PATH_RE.exec(text);
  if (m?.[1] && m[2]) {
    const value = decodeURIComponent(m[2]);
    switch (m[1]) {
      case 'customers':
        return routes.customer(value);
      case 'jobs':
        return routes.job(value);
      case 'suppliers':
        return routes.supplier(value);
      case 'd':
        return routes.document(value);
    }
  }
  if (/^\d+$/.test(text) && text.startsWith(ORACLE_ACCOUNT_PREFIX) && text.length > ORACLE_ACCOUNT_PREFIX.length) {
    return routes.customer(text);
  }
  return null;
}
